import axios from "axios";
import { requestWithMaxReties } from "./utils/requestWithMaxRetries";
import { handleRidersLogin } from "./utils/redirectToLogin";
import projectJson from "./local.json";
import { getPublicProject, isPublicProject } from "./utils/publicProjects";

// if basepath === editor.riders.ai then use prod api, else if basepath === localhost then use dev api else use staging api
let API_URL = process.env.REACT_APP_PROD_API_URL;
if (window.location.hostname === "localhost") {
  API_URL = process.env.REACT_APP_DEV_API_URL;
} else if (window.location.hostname === "editor.riders.ai") {
  API_URL = process.env.REACT_APP_PROD_API_URL;
} else if (window.location.hostname === "editor-staging.riders.ai") {
  API_URL = process.env.REACT_APP_STAGING_API_URL;
} else {
  API_URL = process.env.REACT_APP_DENEYAP_API_URL;
}

const isLocal = window.location.hostname === "localhost";

export const restartSession = async (token: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/restart`, {
        method: "get",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${token}`,
        },
      });

      resolve(data.accessToken);
    } catch (error) {
      reject(error);
    }
  });
};

export const collectAchievement = async (
  token: string,
  achievementId: string
) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.post(
        API_URL + "/api/achievements/achieve/",
        {
          data: achievementId,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const getAchievements = async (token: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.get(API_URL + "/api/achievements/list", {
        headers: {
          authorization: `Token ${token}`,
        },
      });
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const getScore = async (token: string, results: any) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(
        API_URL + "/api/score",
        {
          data: results,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const setQuizResult = async (
  token: string,
  quiz: string,
  results: any
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.post(
        API_URL + "/api/quiz/" + quiz,
        {
          data: results,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(res);
    } catch (error) {
      reject(error);
    }
  });
};

export const getQuizResults = async (token: string, quiz: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.get(API_URL + "/api/quiz/" + quiz, {
        headers: {
          authorization: `Token ${token}`,
        },
      });
      resolve(res);
    } catch (error) {
      resolve([]);
    }
  });
};

export const getQuizQuestions = async (
  token: string,
  quiz: string,
  language: string
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.get(
        API_URL + "/api/quiz/" + quiz + "/questions",
        {
          headers: {
            authorization: `Token ${token}`,
            "Accept-Language": language || "en",
          },
        }
      );
      resolve(res);
    } catch (error) {
      reject(error);
    }
  });
};

export const publishBranch = async (token: string, message: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(
        API_URL + "/api/branches/publish",
        {
          message: message,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const verifyEdusimToken = async (token: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/auth/verify/edusim?token=${token}`
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const verifyToken = async (token: string) => {
  if (token === "playground" && isLocal) {
    return projectJson;
  }

  // Defensive check for public projects
  try {
    if (isPublicProject()) {
      return getPublicProject();
    }
  } catch (error) {
    console.error(error);
  }

  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/auth/verify?token=${token}`
      );

      if (window.location.hostname.includes("riders")) {
        // Second check if user is superuser. If yes, resolve because superuser can access any session.
        try {
          await handleRidersLogin();

          const { data: user_data } = await axios.get(
            `${data.api_url}/api/v1/user`,
            {
              withCredentials: true,
            }
          );

          if (Array.isArray(user_data) && user_data.length > 0 && user_data[0].is_superuser) {
            resolve({ ...data, ridersUserData: user_data[0] });
            return;
          }
        } catch (error) {
          handleRidersLogin();
          reject("invalidToken.loginRequired");
          return;
        }

        // Third check if session is belong for current logged in user.
        try {
          const resp = await axios.get(
            `${data.api_url}/api/v1/editor-session/`,
            {
              withCredentials: true,
            },
          );

          const activeSessions = resp.data.map((s: any) => s.id);
          if (activeSessions.includes(Number(data.session_id))) {
            resolve(data);
          } else {
            reject("invalidToken.activeSessionNotFound");
          }
        } catch (error) {
          handleRidersLogin();
          reject("invalidToken.loginRequired");
        }
      } else {
        resolve(data);
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const refreshToken = async (token: string) => {
  if (token === "playground" && isLocal) {
    return {
      accessToken: "playground",
      status: "verified",
    };
  }
  return new Promise<any>(async (resolve, reject) => {
    try {
      const random = Math.random().toString(36).substring(7);
      const { data } = await requestWithMaxReties({
        method: "get",
        url: `${API_URL}/api/auth/refresh?token=${token}&random=${random}`,
      });
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const idleShutDown = async (token: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.get(`${API_URL}/api/auth/idle`, {
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${token}`,
        },
      });
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const storagePush = async (
  token: string,
  file: string,
  content: string | null
) => {
  return new Promise(async (resolve, reject) => {
    try {
      await axios.post(
        `${API_URL}/api/storage/push`,
        {
          filePath: file,
          fileContent: content,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      resolve(true);
    } catch (error) {
      reject(error);
    }
  });
};

export const getActiveProxyServerAddress = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/settings/active-proxy-server`
      );
      resolve(data.value);
    } catch (error) {
      reject(error);
    }
  });
};

export const pushFileToS3 = async (
  token: string,
  file: string,
  content: string,
  type: string
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await axios.post(
        `${"https://gtkb2pw9xg.execute-api.eu-west-1.amazonaws.com"}/api/aws/write`,
        {
          path: file,
          body: content,
          contentType: type,
        },
        {
          headers: {
            Authorization: token,
          },
        }
      );
      resolve(data.value);
    } catch (error) {
      reject(error);
    }
  });
};

export const setContentId = async (token: string, contentId: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.post(
        API_URL + "/api/auth/set-content-id",
        {
          content_id: contentId,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(res);
    } catch (error) {
      reject(error);
    }
  });
};

export const getModuleProgresses = async (token: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.get(API_URL + "/api/module-progresses", {
        headers: {
          authorization: `Token ${token}`,
        },
      });
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const submitModuleProgress = async (token: string, title: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.post(
        API_URL + "/api/module-progresses/",
        {
          data: title,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const notificationsList = async (
  token: string,
  page: number,
  page_size: number,
  language: string
) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.get(
        API_URL +
        `/api/notifications?page=${page}&page_size=${page_size}&language=${language}`,
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const notificationsMarkAsRead = async (
  token: string,
  encryptedData: string
) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.post(
        API_URL + "/api/notifications/mark_as_read",
        {
          data: encryptedData,
        },
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const notificationsUnreadCount = async (token: string) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const { data } = await axios.get(
        API_URL + "/api/notifications/unread_count",
        {
          headers: {
            authorization: `Token ${token}`,
          },
        }
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};
